'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useReducer,
} from 'react';

type Check = {
  id: string;
};

type State = Array<Check>;

enum ActionType {
  CHECKS_UPDATED,
  CHECK_CREATED,
  CHECK_REMOVED,
}

type Action =
  | {
      type: ActionType.CHECKS_UPDATED;
      data: Array<Check>;
    }
  | {
      type: ActionType.CHECK_CREATED;
      data: Check;
    }
  | {
      type: ActionType.CHECK_REMOVED;
      data: Check;
    };

type CheckersContextType = {
  checks: Array<Check>;
  removeChecked: (cb: (id: string) => void) => void;
  toggleCheck: (id: string) => void;
  checked: (id: string) => boolean;
  updateChecks: (data: Array<Check>) => void;
};

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case ActionType.CHECKS_UPDATED: {
      return [...action.data];
    }

    case ActionType.CHECK_CREATED: {
      return [...state, action.data];
    }

    case ActionType.CHECK_REMOVED: {
      return state.filter(({ id }) => id !== action.data.id);
    }
  }
};

const initialState: Array<Check> = [];

const CheckersContext = createContext<CheckersContextType | null>(null);

interface Props {
  children: ReactNode;
}

const CheckersProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const toggleCheck = useCallback(
    (id: string) => {
      const data = { id };

      const exists = state.findIndex((checked) => checked.id === id) > -1;
      if (exists) {
        dispatch({ type: ActionType.CHECK_REMOVED, data });
      } else {
        dispatch({ type: ActionType.CHECK_CREATED, data });
      }
    },
    [dispatch, state]
  );

  const updateChecks = useCallback((data: Array<Check>) => {
    dispatch({ type: ActionType.CHECKS_UPDATED, data });
  }, []);

  const removeChecked = useCallback(
    (cb: (id: string) => void) => {
      for (const checked of state) {
        cb(checked.id);
        dispatch({ type: ActionType.CHECK_REMOVED, data: { id: checked.id } });
      }
    },
    [dispatch, state]
  );

  const checked = useCallback(
    (id: string) => state.findIndex((checked) => checked.id === id) > -1,
    [state]
  );

  const value = {
    checks: state,
    removeChecked,
    toggleCheck,
    checked,
    updateChecks,
  };

  return (
    <CheckersContext.Provider value={value}>
      {children}
    </CheckersContext.Provider>
  );
};

function useCheckers() {
  const context = useContext(CheckersContext);
  if (!context) throw new Error('useCheckers requires a context!');

  return context;
}

export { CheckersProvider, useCheckers };
